import { JobPipelineTypeSchema, SettingsTypeEnumSchema, SoftwareEnumSchema } from "@domain/schemas/BI";
import { getVisibleSettingsSections } from "@domain/software";

export const PULSAR_STEP_1_ID = "22222222-2222-2222-2222-222222222222";
export const LIBRARY_GENERATION_ID = "55555555-5555-5555-5555-555555555555";

export type RightPanelTabKey = "raw" | "fasta" | SettingsTypeEnumSchema | "archives" | "libraries";

/**
Single source of truth for which Data Explorer tabs are visible and in what order - both the
page that renders the `TabStrip` and every form block that needs to "switch to the X tab" derive
their tab index from this same ordering, instead of each hardcoding a tab's position. Each
SoftwareSettings sub-type the current engine/pipeline combination actually uses gets its own tab,
rather than one shared "Settings" tab with an in-tab switcher.
*/
export const getVisibleRightPanelTabKeys = (
  pipelineType: JobPipelineTypeSchema,
  resumeFromStepId: string | null | undefined,
  isRerunMode: boolean,
  software: SoftwareEnumSchema | null | undefined,
  isAdvancedSettingsExpanded = false,
): RightPanelTabKey[] => {
  const isShowRawFilesTab = !isRerunMode || resumeFromStepId === PULSAR_STEP_1_ID;
  const isShowArchiveTab =
    (pipelineType === JobPipelineTypeSchema.DirectDIA || pipelineType === JobPipelineTypeSchema.LibraryGeneration) &&
    (!isRerunMode || resumeFromStepId === PULSAR_STEP_1_ID || resumeFromStepId === LIBRARY_GENERATION_ID);
  const isShowLibraryTab = pipelineType === JobPipelineTypeSchema.LibraryBased;

  const tabs: RightPanelTabKey[] = [];
  if (isShowRawFilesTab) tabs.push("raw");
  if (pipelineType !== JobPipelineTypeSchema.LibraryBased) tabs.push("fasta");
  for (const section of getVisibleSettingsSections(software, pipelineType, false)) {
    tabs.push(section.settingsType);
  }
  if (isShowArchiveTab) tabs.push("archives");
  if (isAdvancedSettingsExpanded) {
    for (const section of getVisibleSettingsSections(software, pipelineType, true)) {
      tabs.push(section.settingsType);
    }
  }
  if (isShowLibraryTab) tabs.push("libraries");
  return tabs;
};
